// What a save is about to write, asked before it does: the form's fields that differ
// from what was loaded, and what the sections that save through their own editors
// report (`changes()` beside their `save`). Each change can be taken back, or all of
// them at once. Whether a save asks is the editor's `confirmChanges`, else the reader's
// own setting per content type (Content Settings → Show Changes On Save, on by default).
import React, { useEffect, useState } from "react"
import Input from "aldoo-ra/Input"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import { ArrowUturnLeftIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline"

const preferenceKey = (type) => `cms.showChangesOnSave.${String(type || "CMSContent").replace(/\./g, "_")}`

/** Whether a save of this content type lists its changes first (this browser; on unless turned off). */
export const showChangesOnSave = (type) => {
  try {
    return LocalUserPreferences.get(preferenceKey(type)) !== false
  } catch (_) {
    return true
  }
}

export const setShowChangesOnSave = (type, on) => {
  try {
    LocalUserPreferences.set(preferenceKey(type), !!on)
  } catch (_) {
    // the setting is a convenience: a browser that keeps none asks every time
  }
}

// what the record carries that nobody edits: never a change worth naming
const NOISE = new Set(["_id", "__v", "updated", "updatedAt", "created", "createdAt", "localization", "_contentType"])

/** "bookTrailer" / "author_display_name" → "Book trailer" / "Author display name". */
export const humanize = (key) => {
  const words = String(key || "")
    .replace(/[_.-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase()
  return words ? words[0].toUpperCase() + words.slice(1) : ""
}

/** A value as one short line: text (no HTML), a number, a count, or "—". */
export const summarize = (value) => {
  if (value === null || value === undefined || value === "") return "—"
  if (typeof value === "boolean") return value ? "On" : "Off"
  if (typeof value === "number") return String(value)
  if (typeof value === "string") {
    const text = value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
    return text.length > 80 ? `${text.slice(0, 77)}…` : text || "—"
  }
  if (Array.isArray(value)) return value.length === 1 ? "1 item" : `${value.length} items`
  if (typeof value === "object") {
    if (typeof value.value === "string") return value.value
    if (typeof value.name === "string") return value.name
    if (typeof value.label === "string") return value.label
    const count = Object.keys(value).length
    return count ? `${count} ${count === 1 ? "entry" : "entries"}` : "—"
  }
  return String(value)
}

const isRecord = (value) => !!value && typeof value === "object" && !Array.isArray(value)

// a picked option as an editor holds it ({ value, label, … }) stands for its value
const OPTION_KEYS = new Set(["value", "label", "name", "description", "icon", "hint", "color"])
const isOption = (value) => isRecord(value) && "value" in value && ("label" in value || "name" in value) && !isRecord(value.value) && Object.keys(value).every((key) => OPTION_KEYS.has(key))

/**
 * A value as what it means, not how an editor happens to hold it: nothing, "" and an empty list are
 * all nothing; a list of one is that one ("in-stock" and ["in-stock"]); a picked option is its value;
 * a number is its digits (5 and "5"); a record's keys in one order, its editor marks ("_…") left out.
 * Two values that mean the same are no change — an editor that reshapes what it loaded (an id into its
 * option, a list into its first entry) is not the person changing it.
 */
const canonical = (value) => {
  if (value === undefined || value === null || value === "") return null
  if (Array.isArray(value)) {
    const items = value.map(canonical).filter((item) => item !== null)
    if (!items.length) return null
    return items.length === 1 ? items[0] : items
  }
  if (isOption(value)) return canonical(value.value)
  if (isRecord(value)) {
    const out = {}
    for (const key of Object.keys(value).sort()) {
      if (key.startsWith("_")) continue
      const item = canonical(value[key])
      if (item !== null) out[key] = item
    }
    return Object.keys(out).length ? out : null
  }
  if (typeof value === "number") return String(value)
  return value
}
const isEqual = (a, b) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b))
// how an entry of a list reads to a person: a variant by its choices ("S", "S / Red"),
// else its own label or name (a text or a { language: text } map), else its id
const textOf = (value) => (typeof value === "string" ? value : isRecord(value) ? Object.values(value).find((text) => typeof text === "string" && text) || "" : "")
const nameOf = (entry, id) => {
  if (!isRecord(entry)) return id
  const choices = isRecord(entry.options) ? Object.values(entry.options).filter((value) => typeof value === "string" && value) : []
  if (choices.length) return choices.join(" / ")
  return textOf(entry.label) || textOf(entry.name) || textOf(entry.title) || id
}

// what names an entry of a list, so the same one is compared before and after
const idOf = (entry, index) => (isRecord(entry) ? entry.sku || entry.key || entry.pid || entry.id || entry._id || `#${index + 1}` : `#${index + 1}`)

/** The sub-fields of two records that differ, as "stock 1" / "stock 7". */
export const recordDiff = (before = {}, after = {}) => {
  // "_…" is an editor's own working mark, never stored
  const keys = Object.keys({ ...before, ...after }).filter((key) => !NOISE.has(key) && !key.startsWith("_") && !isEqual(before?.[key], after?.[key]))
  return {
    before: keys.map((key) => `${humanize(key).toLowerCase()} ${summarize(before?.[key])}`).join(", "),
    after: keys.map((key) => `${humanize(key).toLowerCase()} ${summarize(after?.[key])}`).join(", "),
  }
}

/**
 * One field's changes: a plain value as itself; a list of records entry by entry (the
 * variants: "Variants · PS-1-S", "stock 1" → "stock 7"; an entry added or removed as a
 * whole); a record by its sub-fields.
 */
function fieldChanges(label, before, after) {
  const listOfRecords = (value) => Array.isArray(value) && value.length > 0 && value.every(isRecord)
  if ((listOfRecords(before) || listOfRecords(after)) && (before == null || Array.isArray(before)) && (after == null || Array.isArray(after))) {
    const was = new Map((before || []).map((entry, index) => [String(idOf(entry, index)), entry]))
    const now = new Map((after || []).map((entry, index) => [String(idOf(entry, index)), entry]))
    const rows = []
    // "Variants · S", its SKU under it when the name is not the id itself
    const named = (id, entry) => {
      const name = nameOf(entry, id)
      return { label: `${label} · ${name}`, detail: name !== id && !id.startsWith("#") ? id : null, itemId: id }
    }
    for (const [id, entry] of now) {
      if (!was.has(id)) rows.push({ ...named(id, entry), before: "—", after: "added" })
      else if (!isEqual(was.get(id), entry)) rows.push({ ...named(id, entry), ...recordDiff(was.get(id), entry) })
    }
    for (const [id, entry] of was) if (!now.has(id)) rows.push({ ...named(id, entry), before: "present", after: "removed" })
    // only the order moved
    return rows.length ? rows : [{ label, before: "order", after: "reordered" }]
  }
  if (isRecord(before) && isRecord(after) && !before._id && !after._id) {
    const diff = recordDiff(before, after)
    return diff.before || diff.after ? [{ label, ...diff }] : []
  }
  return [{ label, before: summarize(before), after: summarize(after) }]
}

/**
 * A form field with one change taken back: the whole field as loaded, or — for an entry
 * of a list (`itemId`) — that entry as loaded, put back where it was, or dropped when it
 * was not there.
 */
export function revertedValue(current, baseline, change) {
  const now = current?.[change.key]
  const was = baseline?.[change.key]
  if (!change.itemId || !Array.isArray(now) && !Array.isArray(was)) return was
  const list = Array.isArray(now) ? [...now] : []
  const at = list.findIndex((entry, index) => String(idOf(entry, index)) === change.itemId)
  const loadedAt = (was || []).findIndex((entry, index) => String(idOf(entry, index)) === change.itemId)
  if (loadedAt < 0) return at < 0 ? list : list.filter((_, index) => index !== at)
  if (at >= 0) list[at] = was[loadedAt]
  else list.splice(Math.min(loadedAt, list.length), 0, was[loadedAt])
  return list
}

/** The form's changes: [{ label, before, after }] for the fields that differ from `baseline`. */
export function formChanges(current, baseline, contentType) {
  if (!baseline) return []
  return Object.keys({ ...baseline, ...current })
    .filter((key) => !NOISE.has(key) && !isEqual(current?.[key], baseline?.[key]))
    .flatMap((key) => {
      const field = (contentType?.fields || []).find((entry) => entry.id === key)
      return fieldChanges(field?.label || humanize(key), baseline?.[key], current?.[key]).map((change) => ({ key, ...change }))
    })
}

// a header cell that stays on top of the scrolling list: its own background, its line drawn
// as a shadow (a border does not travel with a sticky cell)
const STICKY = "sticky top-0 z-10 p-2 font-medium bg-admin_paper shadow-[inset_0_-1px_0_rgba(0,0,0,0.1)]"

// a list longer than this gets a search above it
const SEARCH_FROM = 5

// the host's words (t`English`, t("…%s…", n)); without one, the English as written
const ENGLISH = (text, ...args) => {
  let i = 0
  const s = Array.isArray(text) ? text[0] : String(text ?? "")
  return s.replace(/%s/g, () => (i < args.length ? String(args[i++]) : "%s"))
}

export default function SaveSummaryDialog({ open, changes = [], onCancel, onConfirm, onRevert, onRevertAll, saving = false, t = ENGLISH }) {
  // a field's label as the host says it, its language code kept: "Description (BG)" → "Описание (BG)"
  const labelOf = (label) => {
    const m = String(label ?? "").match(/^(.*?)( \([A-Z]{2}\))$/)
    return m ? `${t(m[1])}${m[2]}` : t(label)
  }
  const valueOf = (value) => (["added", "removed", "present", "On", "Off"].includes(value) ? t(value) : value)
  // the changes that match what is typed: field, SKU, before or after
  const [search, setSearch] = useState("")
  useEffect(() => {
    if (!open) setSearch("")
  }, [open])
  const term = search.trim().toLowerCase()
  const shown = term ? changes.filter((change) => [change.label, change.detail, change.before, change.after].some((text) => String(text ?? "").toLowerCase().includes(term))) : changes
  return (
    <Modal isOpen={open} onClose={onCancel} variant="admin" closeOnEscape styleOverrides={{ container: "w-[min(92vw,720px)] max-h-full md:max-h-[calc(100vh-2rem)] bg-admin_paper rounded-lg border border-admin_bordered shadow-lg overflow-hidden" }}>
      <div className="flex flex-col gap-4 p-5 pt-1" data-testid="save-summary">
        <Typography variant="admin_h4" className="my-0">
          {t`Save these changes?`}
        </Typography>
        {changes.length > SEARCH_FROM && (
          <div data-testid="save-summary-search">
            <Input
              variant="admin"
              value={search}
              placeholder={t("Filter %s changes", changes.length)}
              icon={<MagnifyingGlassIcon className="w-4 h-4" />}
              onChange={(e) => setSearch(e.target.value)}
              showClearButton
              onClear={() => setSearch("")}
            />
          </div>
        )}
        {!changes.length ? (
          <Typography variant="admin_body2" className="opacity-70 my-0">
            {t`No change was found. Saving writes the record as it is.`}
          </Typography>
        ) : (
          // with a search the list keeps its height: filtering does not resize the dialog
          <div className={`${changes.length > SEARCH_FROM ? "h-[55vh]" : "max-h-[55vh]"} overflow-y-auto border border-admin_bordered rounded-lg`}>
            <table className="w-full text-sm">
              <thead>
                {/* the column names stay in view while the list scrolls under them */}
                <tr className="text-left text-admin_text_secondary">
                  <th className={STICKY}>{t`Field`}</th>
                  <th className={STICKY}>{t`Before`}</th>
                  <th className={STICKY}>{t`After`}</th>
                  {onRevert && <th className={`${STICKY} w-10`} />}
                </tr>
              </thead>
              <tbody>
                {term && !shown.length && (
                  <tr>
                    <td colSpan={onRevert ? 4 : 3} className="p-3 text-admin_text_secondary">
                      No change matches “{search.trim()}”.
                    </td>
                  </tr>
                )}
                {shown.map((change, index) => (
                  <tr key={`${change.label}-${index}`} className="border-b border-admin_bordered last:border-b-0 align-top" data-testid="save-summary-row">
                    <td className="p-2 font-medium text-admin_text">
                      <span className="whitespace-nowrap">{labelOf(change.label)}</span>
                      {change.detail && <span className="block font-mono text-[11px] font-normal text-admin_text_secondary">{change.detail}</span>}
                    </td>
                    <td className="p-2 text-admin_text_secondary break-words">{valueOf(change.before) ?? "—"}</td>
                    <td className="p-2 text-admin_text break-words">{valueOf(change.after) ?? "—"}</td>
                    {onRevert && (
                      <td className="p-1 text-right">
                        <Button variant="admin_icon" title={t`Take this change back`} onClick={() => onRevert(change)} disabled={saving} data-testid="save-summary-revert">
                          <ArrowUturnLeftIcon className="w-4 h-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-end gap-2">
          {/* every change taken back: the record as it was loaded */}
          {/* filtered: the matching changes only */}
          {onRevertAll && shown.length > 0 && (
            <Button variant="admin_text" className="mr-auto" onClick={() => onRevertAll(shown)} disabled={saving} data-testid="save-summary-revert-all">
              <ArrowUturnLeftIcon className="w-4 h-4" /> {term ? t("Revert shown (%s)", shown.length) : t`Revert all`}
            </Button>
          )}
          <Button variant="admin_secondary" onClick={onCancel} disabled={saving}>
            {t`Cancel`}
          </Button>
          <Button variant="admin_primary" onClick={onConfirm} disabled={saving} data-testid="save-summary-confirm">
            {saving ? t`Saving…` : t`Save`}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
